import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Pause,
  Play,
} from "lucide-react";

const photo = (name) => `/assets/images/campus/${name}.webp`;
const clinical = (name) => `/assets/images/hospital/${name}.webp`;
export default function HomePageThree() {
  const videoRef = useRef(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const toggleVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) video.play().catch(() => setVideoPlaying(false));
    else video.pause();
  };
  return (
    <div className="univ-home">
      <section className="univ-hero" aria-labelledby="hero-title">
        <video
          ref={videoRef}
          className="univ-hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/assets/video/frame.webp"
          aria-hidden="true"
          onPlay={() => setVideoPlaying(true)}
          onPause={() => setVideoPlaying(false)}
        >
          <source src="/assets/video/hero.mp4" type="video/mp4" />
        </video>
        <div className="univ-hero-shade" />
        <div className="univ-wrap univ-hero-content">
          <p className="univ-eyebrow">
            MEDICAL EDUCATION & PATIENT CARE
          </p>
          <h1 id="hero-title">Takshashila Medical College</h1>
          <p className="univ-hero-description">
            At Takshashila Medical College Hospital, we are more than a centre of
            learning; we are a movement towards transforming healthcare.
          </p>
          <div className="univ-hero-actions">
            <Link className="univ-button univ-button-light" to="/courses-mbbs">
              Explore MBBS <ArrowRight size={17} />
            </Link>
          </div>
        </div>
        <button
          className="univ-video-toggle"
          type="button"
          onClick={toggleVideo}
          aria-label={
            videoPlaying ? "Pause background video" : "Play background video"
          }
        >
          {videoPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>
      </section>
      <section className="univ-intro univ-wrap univ-section">
        <div>
          <p className="univ-eyebrow">WELCOME TO TAKSHASHILA</p>
          <h2>
            Medicine with purpose.
            <br />
            <em>Education with heart.</em>
          </h2>
        </div>
        <div className="univ-intro-copy">
          <p>
            At Takshashila Medical College & Hospital, we are more than a centre
            of learning. Located in Tamil Nadu, our institution brings together
            medical education, research, and compassionate clinical practice.
          </p>
          <p>
            Through experienced faculty and a multidisciplinary teaching
            hospital, we nurture future-ready doctors grounded in ethics,
            empathy, and a deep commitment to humanity.
          </p>
          <Link className="univ-underlined" to="/about-us">
            Get to know Takshashila <ArrowUpRight size={19} />
          </Link>
        </div>
      </section>
      <section className="univ-academic univ-wrap">
        <div className="univ-academic-image">
          <img
            src={clinical("doctors-reviewing")}
            alt="Doctors reviewing a patient's imaging together on the ward at Takshashila"
            loading="lazy"
            width="1600"
            height="1067"
          />
        </div>
        <div className="univ-academic-copy">
          <p className="univ-eyebrow">ACADEMICS & LEARNING</p>
          <h2>
            Your journey
            <br />
            into medicine.
          </h2>
          <p>
            Our MBBS programme builds strong scientific foundations and clinical
            decision-making through progressive bedside exposure, preparing
            students for ethical, evidence-based care.
          </p>
          <Link className="univ-underlined" to="/courses-mbbs">
            Bachelor of Medicine & Bachelor of Surgery{" "}
            <ArrowUpRight size={22} />
          </Link>
          <div className="univ-academic-links">
            <Link to="/academics/academic-calendar">
              Academic calendar <ArrowRight size={16} />
            </Link>
            <Link to="/fee/tuition-fee">
              Tuition fee structure <ArrowRight size={16} />
            </Link>
            <Link to="/faculty">
              Our faculty <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      <section className="univ-section univ-wrap">
        <div className="univ-section-heading">
          <div>
            <p className="univ-eyebrow">EDUCATION & CLINICAL PRACTICE</p>
            <h2>Medical education. Patient care.</h2>
          </div>
          <Link className="univ-underlined" to="/hospital">
            Explore our hospital <ArrowUpRight size={18} />
          </Link>
        </div>
        <div className="univ-stories">
          <Link to="/hospital" className="univ-story">
            <div className="univ-story-image">
              <img
                src="/assets/images/img/infra/beds.webp"
                alt="Patient care facilities at the teaching hospital"
                loading="lazy"
              />
            </div>
            <p className="univ-eyebrow">OUR TEACHING HOSPITAL</p>
            <h3>
              Compassionate clinical care.
              <ArrowUpRight size={22} />
            </h3>
            <p>
              Emergency care, diagnostic services, and specialist treatment
              within our multidisciplinary teaching hospital.
            </p>
          </Link>
          <Link to="/infrastructure" className="univ-story">
            <div className="univ-story-image">
              <img
                src={clinical("operation-theatre")}
                alt="A fully equipped modular operation theatre at Takshashila"
                loading="lazy"
                width="1800"
                height="1200"
              />
            </div>
            <p className="univ-eyebrow">INFRASTRUCTURE</p>
            <h3>
              Campus & hospital facilities.
              <ArrowUpRight size={22} />
            </h3>
            <p>
              View our campus, learning spaces, and teaching hospital facilities.
            </p>
          </Link>
          <Link to="/courses-mbbs" className="univ-story">
            <div className="univ-story-image">
              <img
                src={clinical("doctor-ward-round")}
                alt="A doctor examining a patient during ward rounds at the teaching hospital"
                loading="lazy"
                width="1600"
                height="1067"
              />
            </div>
            <p className="univ-eyebrow">UNDERGRADUATE MEDICAL EDUCATION</p>
            <h3>
              MBBS programme.
              <ArrowUpRight size={22} />
            </h3>
            <p>
              Explore the Bachelor of Medicine and Bachelor of Surgery
              programme and tuition fee details.
            </p>
          </Link>
        </div>
      </section>
      <section className="univ-facilities">
        <div className="univ-wrap">
          <div className="univ-section-heading">
            <div>
              <p className="univ-eyebrow">INSIDE OUR HOSPITAL</p>
              <h2>Built for care, and for learning.</h2>
            </div>
            <Link className="univ-underlined" to="/infrastructure">
              View all facilities <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className="univ-facility-grid">
            {[
              ["inpatient-ward", "Inpatient wards", "Spacious, monitored wards where students learn beside consultants."],
              ["pharmacy", "In-house pharmacy", "A fully stocked pharmacy serving inpatients and outpatients."],
              ["reception", "Reception & ABHA desk", "A single point of help from the moment patients arrive."],
              ["waiting-hall", "Outpatient waiting hall", "Comfortable, well-lit spaces for patients and families."],
            ].map(([img, title, copy]) => (
              <figure className="univ-facility" key={img}>
                <img
                  src={clinical(img)}
                  alt={title + " at Takshashila Medical College Hospital"}
                  loading="lazy"
                  width="1600"
                  height="1067"
                />
                <figcaption>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      <section className="univ-resources">
        <div className="univ-wrap univ-resource-grid">
          <div>
            <p className="univ-eyebrow">INFORMATION & RESOURCES</p>
            <h2>Clarity at every step.</h2>
            <p>
              Essential academic information, institutional disclosures, and
              student support, all within reach.
            </p>
          </div>
          <div className="univ-resource-links">
            {[
              ["MARB NMC Pro-Forma", "/pro-fomra"],
              ["MSR Clause B.1.11 disclosures", "/mci-detail"],
              ["Academic calendar", "/academics/academic-calendar"],
              ["Tuition & fee information", "/fee/tuition-fee"],
              ["Anti-Ragging Committee", "/committee/anti-ragging"],
              ["Enabling Unit for PwBD", "/committee/enabling-unit-pwbd"],
            ].map(([title, to]) => (
              <Link key={to} to={to}>
                {title}
                <ArrowUpRight size={19} />
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="univ-invitation">
        <img
          className="univ-invitation-bg"
          src={photo("campus-invitation")}
          alt=""
          aria-hidden="true"
          loading="lazy"
          width="2000"
          height="769"
        />
        <div className="univ-wrap univ-invitation-inner">
          <div className="univ-invitation-copy">
            <p className="univ-eyebrow">YOUR NEXT CHAPTER</p>
            <h2>Begin with a conversation.</h2>
            <p>
              Whether you are choosing where to study medicine or looking for
              care at our teaching hospital, our team is here to help you take
              the next step.
            </p>
            <div className="univ-invitation-actions">
              <Link className="univ-button univ-button-primary" to="/contact-us">
                Contact us <ArrowUpRight size={19} />
              </Link>
              <Link className="univ-invitation-secondary" to="/courses-mbbs">
                Explore the MBBS programme <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <dl className="univ-invitation-facts">
            <div>
              <dt>Admissions</dt>
              <dd>
                <a href="mailto:deantmch@takshashilauniv.ac.in">
                  deantmch@takshashilauniv.ac.in
                </a>
              </dd>
            </div>
            <div>
              <dt>Enquiries</dt>
              <dd>
                <a href="tel:+918925969546">+91 8925969546</a>
              </dd>
            </div>
            <div>
              <dt>Campus</dt>
              <dd>Ongur, Tindivanam Taluk, Villupuram District</dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
}
